import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { SubmitButton, Overlay } from "@components";
import { TrashIcon } from "@heroicons/react/24/outline";
import type { SxProps } from "@mui/material";
import { useNotification } from "@hooks/useNotifications";

export const DeleteButton = ({
  id,
  sx,
  deleteService,
  label,
  skipNavigate,
}: {
  id: string | number;
  sx?: SxProps;
  deleteService: any;
  label: string;
  skipNavigate?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const { showError, showSuccess } = useNotification();

  const handleClickOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  const handleClose = () => {
    if (abortController) {
      abortController.abort({ reason: "Operation cancelled by the user" });
      setAbortController(null);
    }

    setOpen(false);
  };

  const handleDelete = async () => {
    try {
      const controller = new AbortController();
      setAbortController(controller);
      setIsProcessing(true);

      await deleteService(id, controller);

      if (!controller?.signal?.aborted) {
        // Refresh or navigate to the previous page
        navigate(skipNavigate ? 0 : -1);

        showSuccess("Successfully deleted!");
      }
    } catch (error) {
      console.error(error);
      const message = "Delete was failed";

      if (error.response?.data?.includes("Amazon.CognitoIdentityProvider.Model.UserNotFoundException")) {
        navigate(-1);
        return;
      }

      showError(message);
    } finally {
      setAbortController(null);
      setOpen(false);
      setIsProcessing(false);
    }
  };

  return (
    <Box sx={sx}>
      <Overlay isVisible={isProcessing} />

      <IconButton onClick={handleClickOpen} color="error">
        <TrashIcon width={18} height={18} />
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm {label} deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this {label.toLocaleLowerCase()}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isProcessing}>
            Cancel
          </Button>
          <SubmitButton isProcessing={isProcessing} color="error" onClick={handleDelete}>
            Delete
          </SubmitButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
